import type { RememberedMissionReferral } from './missionReferralRegistry';

interface Listing {
  branchId?: string;
  periodMissionConfigurationIds?: string[];
  geocoordinate?: { x?: number; y?: number };
  tags?: string;
  distance?: number;
  canAccept?: boolean;
  upcomingPeriod?: boolean;
  isReferral?: boolean;
  isPinned?: boolean;
}
interface DiscoveryArea {
  center?: { lat: number; lng: number };
  topLeft?: { lat: number; lng: number };
  bottomRight?: { lat: number; lng: number };
  categories: string[];
  onlyAcceptable: boolean;
  showUpcoming: boolean;
}
const normalize = (text: string) => text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
const distance = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const radians = Math.PI / 180;
  const sinLat = Math.sin((b.lat - a.lat) * radians / 2);
  const sinLng = Math.sin((b.lng - a.lng) * radians / 2);
  return 6371000 * 2 * Math.asin(Math.sqrt(Math.min(1,
    sinLat * sinLat + Math.cos(a.lat * radians) * Math.cos(b.lat * radians) * sinLng * sinLng)));
};

/** Merge reserved inventory without expanding the shopper's current search area. */
export function mergeReferralDiscovery<T extends Listing>(
  listings: T[], records: readonly RememberedMissionReferral[], hydrate: (data: Record<string, any>) => T,
  area: DiscoveryArea, now = Date.now(),
): T[] {
  const pending = records.filter(record => Date.parse(record.expiresAt) > now);
  const decorated = listings.map(listing => Object.assign(Object.create(Object.getPrototypeOf(listing)), listing, {
    isReferral: pending.some(record => record.branchId === listing.branchId && listing.periodMissionConfigurationIds?.includes(record.periodMissionConfigurationId)),
  })) as T[];
  const missing = new Map<string, T>();
  for (const record of [...pending].sort((a, b) => b.openedAt - a.openedAt)) {
    if (!record.listing || decorated.some(listing => listing.branchId === record.branchId && listing.periodMissionConfigurationIds?.includes(record.periodMissionConfigurationId))) continue;
    const id = `${record.branchId}:${record.periodMissionConfigurationId}`;
    if (missing.has(id)) continue;
    const listing = hydrate(record.listing);
    const lat = listing.geocoordinate?.y;
    const lng = listing.geocoordinate?.x;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    if (area.categories.length && !area.categories.some(category => normalize(listing.tags ?? '').includes(normalize(category)))) continue;
    if (area.onlyAcceptable && !listing.canAccept || !area.showUpcoming && listing.upcomingPeriod) continue;
    if (area.center) listing.distance = distance(area.center, { lat: lat!, lng: lng! });
    if (area.topLeft && area.bottomRight) {
      const withinLng = area.topLeft.lng <= area.bottomRight.lng
        ? lng! >= area.topLeft.lng && lng! <= area.bottomRight.lng
        : lng! >= area.topLeft.lng || lng! <= area.bottomRight.lng;
      if (lat! > area.topLeft.lat || lat! < area.bottomRight.lat || !withinLng) continue;
    }
    missing.set(id, Object.assign(listing, { isReferral: true }));
  }
  let additions = [...missing.values()];
  if (!area.topLeft || !area.bottomRight) {
    // Same radius policy as PortalCore MissionListingHelper.GetInitialRadiusMeters.
    const distances = [...decorated, ...additions].filter(item => !item.isPinned).map(item => item.distance).filter((value): value is number => Number.isFinite(value));
    const radius = Math.max(30000, Math.min(...distances) + 20000);
    additions = additions.filter(item => item.distance !== undefined && item.distance <= radius);
  }
  return [...decorated, ...additions];
}
